"use client";

import type { Key } from "react";
import type { LocationItem } from "./types";

import { useState } from "react";
import { useMediaQuery } from "usehooks-ts";
import { LocationsList } from "./LocationsList";
import { LocationsMap } from "./LocationsMap";
import { SearchArea } from "./SearchArea";

interface ResultsViewProps {
  locations: LocationItem[];
  searchQuery?: string;
}

export function ResultsView({ locations, searchQuery }: ResultsViewProps) {
  const [selectedLocationId, setSelectedLocationId] = useState<Key | null>(
    null,
  );
  const [previousSearchQuery, setPreviousSearchQuery] = useState(searchQuery);

  if (searchQuery !== previousSearchQuery) {
    setPreviousSearchQuery(searchQuery);
    setSelectedLocationId(null);
  }

  // Keep this breakpoint in sync with the `hidden lg:block` class on the map
  // wrapper below. We need both because `useMediaQuery` returns `false` on the
  // server while the client may evaluate it as `true`, causing a hydration
  // mismatch if we rely on it alone to conditionally render the map. The CSS
  // class ensures the server/client markup always matches; this hook drives the
  // selection-related props that don't affect the rendered HTML structure.
  const isMapVisible = useMediaQuery("(min-width: 1024px)");

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[464fr_685fr] lg:gap-x-16 xl:gap-x-28">
      {/* Left column: search + results */}
      <div className="flex flex-col gap-12 lg:gap-10">
        <SearchArea
          defaultQuery={searchQuery}
          heading="Find your local office"
          horizontalBreakpoint="xl"
        />

        <hr className="hidden border-new-beginnings-tints-600 lg:block" />

        <LocationsList
          locations={locations}
          onSelectLocation={isMapVisible ? setSelectedLocationId : undefined}
          searchQuery={searchQuery}
          selectedLocationId={isMapVisible ? selectedLocationId : undefined}
        />
      </div>

      {/* Right column: map (desktop only, sticky while scrolling the list).
         Height = viewport − utility bar (3rem) − navbar (5rem) − spacing above/below (4rem). */}
      <div className="hidden lg:block">
        <div className="sticky top-[10rem] h-[calc(100dvh-12rem)] w-full">
          <LocationsMap
            locations={locations}
            onSelectLocation={setSelectedLocationId}
            selectedLocationId={selectedLocationId}
          />
        </div>
      </div>
    </div>
  );
}
